import { z } from 'zod';
import { isoDateTime, jsonObject, uuid } from './primitives';
import { toolCallIntent, toolResult } from './tool';

/**
 * CanonicalEvent (§8.2, §11.1, §12.1). What a connector's `poll()` emits and the
 * ingestion pipeline stores in `events`. Every producer supplies a non-null
 * `dedupeKey` (§6.4) so dedup can never be bypassed.
 */
export const eventSource = z.enum(['connector', 'schedule', 'manual', 'platform']);
export type EventSource = z.infer<typeof eventSource>;

export const canonicalEvent = z.object({
  type: z.string().min(1), // e.g. "email.received"
  source: eventSource,
  connectionId: uuid.nullable().optional(),
  subject: jsonObject, // stable identity of the thing the event is about
  data: jsonObject, // event payload
  /** `source:connection_id:external_id` or `…:sha256(content)` (§6.4). Never null. */
  dedupeKey: z.string().min(1),
  occurredAt: isoDateTime,
});
export type CanonicalEvent = z.infer<typeof canonicalEvent>;

/**
 * WakeEvent (§7.3). Every episode begins with exactly one wake. Discriminated by
 * `kind`. `approval_outcome` carries the platform-executed result (I3/§10) — the
 * model never re-proposes the approved call.
 */
export const wakeEvent = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('trigger'),
    triggerKind: z.enum(['event', 'schedule', 'manual']),
    event: canonicalEvent.optional(),
  }),
  z.object({
    kind: z.literal('timer'),
    firedAt: isoDateTime,
  }),
  z.object({
    kind: z.literal('event'),
    event: canonicalEvent,
  }),
  z.object({
    kind: z.literal('approval_outcome'),
    approvalId: uuid,
    proposedCall: toolCallIntent,
    outcome: z.discriminatedUnion('decision', [
      z.object({ decision: z.literal('approved'), result: toolResult }),
      z.object({ decision: z.literal('rejected'), note: z.string().optional() }),
      z.object({ decision: z.literal('expired') }),
    ]),
  }),
  z.object({
    kind: z.literal('user_input'),
    input: z.string(),
  }),
]);
export type WakeEvent = z.infer<typeof wakeEvent>;
